import CommunicationMethod from '@/entities/CommunicationMethod.js';
import { useI18n } from '@/hooks/useI18n.ts';
import { getFullName } from '@/utils/index.js';
import ActivixDate from '@/value-objects/ActivixDate.js';

export type CommunicatingUser = {
    id: number;
    first_name: string;
    last_name: string;
};

export type CommunicatingLead = {
    id: number;
    communication_method_id: number | null;
    communicating_user: CommunicatingUser;
};

export type RecentCommunication = {
    id: number;
    communication_method_id: number | null;
    created_at: string;
    user: CommunicatingUser;
};

const communicatingIconByMethod: Record<number, string> = {
    [CommunicationMethod.PHONE]: 'regular/phone',
    [CommunicationMethod.SMS]: 'regular/messages-bubble-typing-1',
    [CommunicationMethod.VIDEO]: 'regular/meeting-camera',
    [CommunicationMethod.EMAIL]: 'regular/email-action-unread',
};

const communicatingMessageKeyByMethod: Record<number, string> = {
    [CommunicationMethod.PHONE]: 'leadCommunicating.current.phone.description',
    [CommunicationMethod.SMS]: 'leadCommunicating.current.sms.description',
    [CommunicationMethod.VIDEO]: 'leadCommunicating.current.video.description',
    [CommunicationMethod.EMAIL]: 'leadCommunicating.current.email.description',
};

const recentCommunicationMessageKeyByMethod: Record<number, string> = {
    [CommunicationMethod.PHONE]: 'leadCommunicating.recent.phone.description',
    [CommunicationMethod.SMS]: 'leadCommunicating.recent.sms.description',
    [CommunicationMethod.EMAIL]: 'leadCommunicating.recent.email.description',
};

export const useLeadCommunicating = () => {
    const { t } = useI18n();

    const communicationMethodIcon = (communicationMethodId: number | null): string => {
        if (communicationMethodId === null) {
            return '';
        }

        return communicatingIconByMethod[communicationMethodId] || '';
    };

    const communicatingMessage = (communicatingLead: CommunicatingLead | null): string => {
        if (!communicatingLead) {
            return '';
        }

        if (communicatingLead.communication_method_id === null) {
            return '';
        }

        const userName = getFullName(communicatingLead.communicating_user);
        const translationKey =
            communicatingMessageKeyByMethod[communicatingLead.communication_method_id];

        if (!translationKey) {
            return '';
        }

        return t(translationKey, {
            userName,
        });
    };

    const communicatingIcon = (communicatingLead: CommunicatingLead | null): string => {
        if (!communicatingLead) {
            return '';
        }

        return communicationMethodIcon(communicatingLead.communication_method_id);
    };

    const recentCommunicationMessage = (
        recentCommunication: RecentCommunication | null
    ): string => {
        if (!recentCommunication) {
            return '';
        }

        if (recentCommunication.communication_method_id === null) {
            return '';
        }

        const translationKey =
            recentCommunicationMessageKeyByMethod[recentCommunication.communication_method_id];

        if (!translationKey) {
            return '';
        }

        return t(translationKey, {
            userName: getFullName(recentCommunication.user),
            timeAgo: new ActivixDate(
                recentCommunication.created_at
            ).formatDistanceToNowInContextTimezone(),
        });
    };

    const recentCommunicationIcon = (recentCommunication: RecentCommunication | null): string => {
        if (!recentCommunication) {
            return '';
        }

        return communicationMethodIcon(recentCommunication.communication_method_id);
    };

    return {
        communicatingMessage,
        communicatingIcon,
        recentCommunicationMessage,
        recentCommunicationIcon,
    };
};
